import { Member_Role } from '@gen/chat/v1/common_pb';
import type { ConversationRecord, MemberRecord } from '@/store/types';

/**
 * Role and permission decisions, in one place.
 *
 * These are *interface* decisions and nothing more. Section 05 is explicit that
 * an offline client cache may temporarily reflect stale permissions and that the
 * server remains authoritative for new access, so nothing here is a security
 * boundary: it decides which controls are worth showing, and the server decides
 * whether the resulting request is allowed. Showing an action the server will
 * refuse is a poor experience; hiding one the server would allow is a bug. Both
 * are recoverable, which is exactly why this logic may live on the client.
 *
 * The flag bits are the Section 06 admin flags in their listed order. They are
 * a bitfield in `Member.permissions`, an int64 on the wire, and the client only
 * ever reads the low bits — `Number` is exact to 2^53, far above the seven flags
 * defined here and the room left for Phase 6.
 */
export const PERMISSION_MANAGE_MEMBERS = 1 << 0;
export const PERMISSION_MANAGE_TOPICS = 1 << 1;
export const PERMISSION_PIN = 1 << 2;
export const PERMISSION_DELETE_OTHERS = 1 << 3;
export const PERMISSION_MANAGE_INVITES = 1 << 4;
/** Inactive until Phase 6; never offered as a control before then. */
export const PERMISSION_MANAGE_CALLS = 1 << 5;

/** Section 06 channel admin flags, which are a separate set from the group ones. */
export const PERMISSION_PUBLISH = 1 << 0;
export const PERMISSION_EDIT_POSTS = 1 << 1;
export const PERMISSION_DELETE_POSTS = 1 << 2;
export const PERMISSION_INVITE = 1 << 3;
export const PERMISSION_LINK_DISCUSSION = 1 << 4;

/**
 * The role names that travel in `Conversation.own_role`.
 *
 * The wire field is a string rather than the `Member.Role` enum, so it is
 * normalized here once instead of being compared case-sensitively at a dozen
 * call sites.
 */
export type RoleName = 'OWNER' | 'ADMIN' | 'MEMBER' | 'RESTRICTED' | 'NONE';

export function roleFromName(value: string): RoleName {
  switch (value.toUpperCase()) {
    case 'OWNER':
      return 'OWNER';
    case 'ADMIN':
      return 'ADMIN';
    case 'MEMBER':
      return 'MEMBER';
    case 'RESTRICTED':
      return 'RESTRICTED';
    default:
      return 'NONE';
  }
}

export function roleFromEnum(value: number): RoleName {
  switch (value) {
    case Member_Role.OWNER:
      return 'OWNER';
    case Member_Role.ADMIN:
      return 'ADMIN';
    case Member_Role.MEMBER:
      return 'MEMBER';
    case Member_Role.RESTRICTED:
      return 'RESTRICTED';
    default:
      return 'NONE';
  }
}

export function roleToEnumName(role: RoleName): string {
  return role === 'NONE' ? 'MEMBER' : role;
}

export interface Viewer {
  role: RoleName;
  permissions: number;
}

/** The viewer's own standing in a conversation, from the two places it is known. */
export function viewerFor(
  conversation: ConversationRecord | null,
  own: MemberRecord | null,
): Viewer {
  if (own) {
    return { role: roleFromEnum(own.role), permissions: own.permissions };
  }
  return { role: roleFromName(conversation?.ownRole ?? ''), permissions: 0 };
}

/** Section 06: "Owner has all permissions." */
function has(viewer: Viewer, flag: number): boolean {
  return viewer.role === 'OWNER' || (viewer.role === 'ADMIN' && (viewer.permissions & flag) !== 0);
}

export function canManageMembers(viewer: Viewer): boolean {
  return has(viewer, PERMISSION_MANAGE_MEMBERS);
}

export function canManageTopics(viewer: Viewer): boolean {
  return has(viewer, PERMISSION_MANAGE_TOPICS);
}

export function canPin(viewer: Viewer): boolean {
  return has(viewer, PERMISSION_PIN);
}

export function canDeleteOthers(viewer: Viewer): boolean {
  return has(viewer, PERMISSION_DELETE_OTHERS);
}

export function canManageInvites(viewer: Viewer): boolean {
  return has(viewer, PERMISSION_MANAGE_INVITES);
}

/** Only the owner may grant or revoke admin, or transfer ownership. */
export function canChangeRoles(viewer: Viewer): boolean {
  return viewer.role === 'OWNER';
}

/** Section 06: "Members may send unless restricted." */
export function canSend(viewer: Viewer): boolean {
  return viewer.role === 'OWNER' || viewer.role === 'ADMIN' || viewer.role === 'MEMBER';
}

/** Channel publishing belongs to OWNER/ADMIN only. */
export function canPublish(viewer: Viewer): boolean {
  return viewer.role === 'OWNER' || (viewer.role === 'ADMIN' && (viewer.permissions & PERMISSION_PUBLISH) !== 0);
}

export function canLinkDiscussion(viewer: Viewer): boolean {
  return (
    viewer.role === 'OWNER' ||
    (viewer.role === 'ADMIN' && (viewer.permissions & PERMISSION_LINK_DISCUSSION) !== 0)
  );
}

/** Section 06: administrators are exempt from slow mode. */
export function exemptFromSlowMode(viewer: Viewer): boolean {
  return viewer.role === 'OWNER' || viewer.role === 'ADMIN';
}
